import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useRegion } from '../context/RegionContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import {
  Building2,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Gift,
  ArrowRight,
  Loader2,
  CreditCard,
  Shield,
  FileText,
  Users,
  Bell,
  Wrench,
  Globe,
  Sparkles,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Subscribe() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { region, formatPrice, calculateSubscriptionPrice } = useRegion();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [trialStatus, setTrialStatus] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('standard');
  const token = localStorage.getItem('session_token');

  useEffect(() => {
    if (user && token) {
      fetchTrialStatus();
    } else {
      setLoading(false);
    }
  }, [user, token]);

  const fetchTrialStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/trial/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTrialStatus(data);
      }
    } catch (error) {
      console.error('Error fetching trial status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!user) {
      navigate('/login?redirect=/subscribe');
      return;
    }

    setProcessing(true);
    try {
      // Convert trial to subscription
      const response = await fetch(`${API_URL}/api/trial/convert-to-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Subscription activated! Welcome to BodyCorp365.');
        // Redirect to appropriate dashboard
        if (user.role === 'managing_agent') {
          navigate('/agent');
        } else if (user.role === 'trustee') {
          navigate('/trustee-hub');
        } else {
          navigate('/dashboard');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to activate subscription');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const features = [
    { icon: Users, text: 'Centralise all communication' },
    { icon: Wrench, text: 'Track and resolve maintenance issues' },
    { icon: FileText, text: 'Store and share important documents' },
    { icon: Shield, text: 'Run secure digital voting' },
  ];

  // Determine status display
  const getStatusDisplay = () => {
    if (!trialStatus) return null;

    const { status, days_remaining, is_expired, is_grace_period, grace_days_remaining } = trialStatus;

    if (status === 'active' || status === 'expiring_soon') {
      return {
        type: 'trial',
        icon: <Clock className="w-6 h-6 text-blue-500" />,
        title: 'Trial Active',
        message: `You have ${days_remaining} day${days_remaining !== 1 ? 's' : ''} remaining in your free trial.`,
        color: 'blue',
        urgent: days_remaining <= 3
      };
    }

    if (status === 'grace_period') {
      return {
        type: 'grace',
        icon: <AlertTriangle className="w-6 h-6 text-amber-500" />,
        title: 'Trial Expired - Grace Period',
        message: `Your trial has ended — activate now to retain access and data. You have ${grace_days_remaining || 14} days before your data is deleted.`,
        color: 'amber',
        urgent: true
      };
    }

    if (status === 'subscription_required') {
      return {
        type: 'expired',
        icon: <AlertTriangle className="w-6 h-6 text-red-500" />,
        title: 'Your Free Trial Has Ended',
        message: 'To continue using BodyCorp365, please activate your subscription. Your building data will be preserved for a limited time.',
        color: 'red',
        urgent: true
      };
    }

    if (!trialStatus.is_trial) {
      return {
        type: 'subscribed',
        icon: <CheckCircle2 className="w-6 h-6 text-emerald-500" />,
        title: 'Active Subscription',
        message: 'Your subscription is active. Thank you for being a BodyCorp365 customer!',
        color: 'emerald',
        urgent: false
      };
    }

    return null;
  };

  const statusDisplay = getStatusDisplay();

  // Pricing based on region
  const pricing = region?.code === 'INTERNATIONAL'
    ? { symbol: '$', price: 12, currency: 'USD', minPrice: 250, example: '$250/month (for 10 units)' }
    : { symbol: 'R', price: 35, currency: 'ZAR', minPrice: 750, example: 'R750/month (for 10 units)' };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900" data-testid="subscribe-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 text-white flex-shrink-0">
            <Building2 className="w-8 h-8" />
            <span className="text-xl font-bold hidden sm:inline">BodyCorp365</span>
          </Link>
          {user ? (
            <Button 
              variant="outline" 
              className="text-white border-white/30 hover:bg-white/10 text-sm sm:text-base whitespace-nowrap"
              onClick={() => navigate(user.role === 'managing_agent' ? '/agent' : '/dashboard')}
            >
              <span className="hidden sm:inline">Back to </span>Dashboard
            </Button>
          ) : (
            <Link to="/login">
              <Button variant="outline" className="text-white border-white/30 hover:bg-white/10">
                Login
              </Button>
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-12">
        {/* Status Banner (for logged in users) */}
        {user && statusDisplay && (
          <div className={`mb-8 p-6 rounded-2xl ${
            statusDisplay.color === 'red' ? 'bg-red-500/20 border border-red-500/30' :
            statusDisplay.color === 'amber' ? 'bg-amber-500/20 border border-amber-500/30' :
            statusDisplay.color === 'emerald' ? 'bg-emerald-500/20 border border-emerald-500/30' :
            'bg-blue-500/20 border border-blue-500/30'
          }`}>
            <div className="flex items-start gap-4">
              {statusDisplay.icon}
              <div>
                <h3 className="text-lg font-semibold text-white">{statusDisplay.title}</h3>
                <p className="text-blue-100">{statusDisplay.message}</p>
              </div>
            </div>
          </div>
        )}

        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
            <Sparkles className="w-5 h-5 text-amber-400" />
            <span className="font-medium text-white">Secure Your Building Today</span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            Keep Your Building Running Smoothly
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Continue managing your building without interruptions.
          </p>
        </div>

        {/* Pricing Card */}
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Plan Card */}
          <Card className="bg-white shadow-2xl border-2 border-blue-500" data-testid="pricing-card">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-blue-600" />
              </div>
              <CardTitle className="text-2xl">Standard Plan</CardTitle>
              <CardDescription>Full access to all features</CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="text-center mb-6">
                <div className="flex items-end justify-center gap-1">
                  <span className="text-5xl font-bold text-slate-900">{pricing.symbol}{pricing.price}</span>
                  <span className="text-slate-500 text-lg pb-1">/ unit / month</span>
                </div>
                <p className="text-slate-500 mt-2">Billed annually (12 months)</p>
                <p className="text-blue-600 font-medium mt-1">
                  Minimum {pricing.symbol}{pricing.minPrice}/month
                </p>
                {region && (
                  <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-full text-sm text-slate-600">
                    <Globe className="w-4 h-4" />
                    <span>{region.flag} Pricing in {region.currency}</span>
                  </div>
                )}
              </div>

              <ul className="space-y-3 mb-8">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-700">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                      <feature.icon className="w-4 h-4 text-blue-600" />
                    </div>
                    <span>{feature.text}</span>
                  </li>
                ))}
              </ul>

              <Button
                className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-lg font-semibold"
                onClick={handleSubscribe}
                disabled={processing || (trialStatus && !trialStatus.is_trial)}
                data-testid="subscribe-button"
              >
                {processing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : trialStatus && !trialStatus.is_trial ? (
                  <>
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Already Subscribed
                  </>
                ) : (
                  <>
                    <Shield className="w-5 h-5 mr-2" />
                    Activate My Subscription
                  </>
                )}
              </Button>

              {/* Urgency message for expired trials */}
              {trialStatus && (trialStatus.status === 'grace_period' || trialStatus.status === 'subscription_required') && (
                <p className="text-center text-sm text-amber-600 bg-amber-50 rounded-lg p-3 mt-4 font-medium">
                  Your trial has ended — activate now to retain access and data.
                </p>
              )}

              <p className="text-center text-sm text-slate-500 mt-4">
                Cancel anytime. Your data is always yours.
              </p>

              {/* Secondary CTA */}
              <div className="mt-4 pt-4 border-t text-center">
                <a 
                  href="mailto:hello@bodycorp365.com" 
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium hover:underline"
                >
                  Contact us for assistance
                </a>
              </div>
            </CardContent>
          </Card>

          {/* Benefits Card */}
          <div className="space-y-6">
            <Card className="bg-white/10 backdrop-blur border-white/20">
              <CardHeader>
                <CardTitle className="text-white">With BodyCorp365, you can:</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-white">Centralise all communication</p>
                    <p className="text-sm text-blue-200">Keep residents, trustees and agents connected</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-white">Track and resolve maintenance issues</p>
                    <p className="text-sm text-blue-200">Never miss a maintenance request again</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-white">Store and share important documents</p>
                    <p className="text-sm text-blue-200">Secure document vault for all building records</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-white">Run secure digital voting</p>
                    <p className="text-sm text-blue-200">AGMs and resolutions made easy</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pricing Example */}
            <Card className="bg-white/10 backdrop-blur border-white/20">
              <CardHeader>
                <CardTitle className="text-white text-lg">Pricing Examples</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-blue-100">
                  <div className="flex justify-between">
                    <span>10 units</span>
                    <span className="font-medium text-white">
                      {pricing.symbol}{Math.max(pricing.price * 10, pricing.minPrice)}/month
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>25 units</span>
                    <span className="font-medium text-white">
                      {pricing.symbol}{pricing.price * 25}/month
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>50 units</span>
                    <span className="font-medium text-white">
                      {pricing.symbol}{pricing.price * 50}/month
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>100 units</span>
                    <span className="font-medium text-white">
                      {pricing.symbol}{pricing.price * 100}/month
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* FAQ */}
            <Card className="bg-white/10 backdrop-blur border-white/20">
              <CardHeader>
                <CardTitle className="text-white text-lg">Common Questions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="font-medium text-white">What happens to my trial data?</p>
                  <p className="text-sm text-blue-200">All your data is preserved when you subscribe. Nothing is lost.</p>
                </div>
                <div>
                  <p className="font-medium text-white">Can I cancel anytime?</p>
                  <p className="text-sm text-blue-200">Yes, you can cancel your subscription at any time. No long-term commitments.</p>
                </div>
                <div>
                  <p className="font-medium text-white">How is billing calculated?</p>
                  <p className="text-sm text-blue-200">Based on the number of units in your building(s), billed annually.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Not logged in CTA */}
        {!user && (
          <div className="mt-12 text-center">
            <p className="text-blue-100 mb-4">Don't have an account yet?</p>
            <Link to="/free-trial">
              <Button 
                size="lg" 
                className="bg-white text-blue-900 hover:bg-blue-50 rounded-full px-8"
              >
                <Gift className="w-5 h-5 mr-2" />
                Start Your Free Trial
              </Button>
            </Link>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 text-center text-blue-200 text-sm">
        <p>© 2026 BodyCorp365. All rights reserved.</p>
        <p className="mt-2">
          Questions? Contact us at{' '}
          <a href="mailto:hello@bodycorp365.com" className="text-white hover:underline">
            hello@bodycorp365.com
          </a>
        </p>
      </footer>
    </div>
  );
}
